import { PromoCodeForm } from "@/components/forms/promo-code-form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { DiscountType, PromoCodeDataSchema, PromoCode, PromoCodeData } from "@/core/entities/promo-code";
import { ProductType } from "@/core/entities/product";
import { useUpdatePromoCode } from "@/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

interface PromoCodeEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promoCode: PromoCode | null;
}

export const PromoCodeEditDialog = ({
  open,
  onOpenChange,
  promoCode,
}: PromoCodeEditDialogProps) => {
  const [showCloseAlert, setShowCloseAlert] = useState(false);

  const updatePromoCodeMutation = useUpdatePromoCode();

  const form = useForm<PromoCodeData>({
    resolver: zodResolver(PromoCodeDataSchema),
    defaultValues: {
      name: "",
      code: "",
      discount: {
        type: DiscountType.PERCENTAGE,
        value: 0,
      },
      expirationDate: undefined,
      enabledFor: ProductType.BOTH,
    },
  });

  useEffect(() => {
    if (promoCode && open) {
      form.reset({
        name: promoCode.name,
        code: promoCode.code,
        discount: promoCode.discount,
        expirationDate: promoCode.expirationDate,
        enabledFor: promoCode.enabledFor,
      });
    }
  }, [promoCode, open, form]);

  const onSubmit = async () => {
    if (!promoCode) return;

    const formData = form.getValues();
    const data = PromoCodeDataSchema.parse(formData);
    try {
      await updatePromoCodeMutation.mutateAsync({
        id: promoCode.id,
        data,
      });
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update promo code:", error);
      throw error;
    }
  };

  const handleClose = () => {
    if (form.formState.isDirty) {
      setShowCloseAlert(true);
    } else {
      onOpenChange(false);
    }
  };

  const confirmClose = () => {
    setShowCloseAlert(false);
    onOpenChange(false);
  };

  if (!promoCode) {
    return null;
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent
          side="right"
          className="w-full md:max-w-2xl max-w-full overflow-y-auto p-4 sm:p-8 md:p-12"
        >
          <SheetHeader className="p-0">
            <SheetTitle>Edit Promo Code</SheetTitle>
            <SheetDescription>
              Update the promo code information below.
            </SheetDescription>
          </SheetHeader>
          <PromoCodeForm
            form={form}
            onCancel={handleClose}
            onSubmit={onSubmit}
            isLoading={updatePromoCodeMutation.isPending}
            submitText="Update Promo Code"
          />
        </SheetContent>
      </Sheet>

      <AlertDialog open={showCloseAlert} onOpenChange={setShowCloseAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without
              saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmClose}>
              Close without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
